import { useLayoutEffect, useState } from 'react';

const MOUSE_Y_THRESHOLD = 10;
const RESPONSIVE_WIDTH = 500;
const PIXEL_SPEED_THRESHOLD = 250;

export function useExitIntent(): {
  isTryingToExit: boolean;
  resetIntent: Function;
} {
  const [isTryingToExit, setIsTryingToExit] = useState(false);

  const resetIntent = () => {
    setIsTryingToExit(false);
  };

  const toggleIntentOn = () => {
    setIsTryingToExit(true);
  };

  useLayoutEffect(() => {
    let mouseLeaveTimeout: NodeJS.Timeout | undefined;
    let newPosition = window.scrollY;
    let currentSpeed: number;

    function onMouseLeave(event: MouseEvent) {
      if (event.clientY <= MOUSE_Y_THRESHOLD && !isTryingToExit) {
        mouseLeaveTimeout = setTimeout(() => toggleIntentOn(), 100);
      } else {
        if (mouseLeaveTimeout) {
          clearTimeout(mouseLeaveTimeout);
        }
      }
    }

    function onScroll(): void {
      // This checks if the user on mobile scrolled "fast" in a short timeframe by checking the pixels traveled.
      if (window.innerWidth <= RESPONSIVE_WIDTH) {
        setTimeout(() => {
          newPosition = window.scrollY;
        }, 100);
        currentSpeed = newPosition - window.scrollY;
        if (currentSpeed > PIXEL_SPEED_THRESHOLD) {
          toggleIntentOn();
        }
      }
    }

    function onBeforeUnload(event: BeforeUnloadEvent): void {
      event.preventDefault();
      event.returnValue = 'Are you sure you want to exit?';
      toggleIntentOn();
    }

    document.body.addEventListener('mouseleave', onMouseLeave, {
      capture: true,
    });
    document.addEventListener('scroll', onScroll);
    window.addEventListener('beforeunload', onBeforeUnload, { capture: true });
    window.addEventListener('popstate', toggleIntentOn, { capture: true });

    return () => {
      document.body.removeEventListener('mouseleave', onMouseLeave);
      document.removeEventListener('scroll', onScroll);
      window.removeEventListener('beforeunload', onBeforeUnload);
      window.removeEventListener('popstate', toggleIntentOn);
    };
  }, []);

  return {
    isTryingToExit,
    resetIntent,
  };
}
